import {
    Typography,
    DialogContent,
    TextField,
    Button,
    Box,
    Stack,
    MenuItem,
    ListItemIcon,
    FormControlLabel,
    Checkbox,
    Tooltip,
    useTheme,
    Radio,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useErrorDisplay } from "../components/ErrorDisplay"
import BusySpinner from "../components/BusySpinner";
import { IconButtonExProps } from "../components/IconButtonEx";
import { DataGridPro } from '@mui/x-data-grid-pro';
import { I18N } from "../I18N";
import I18NText from "../components/I18NText";
import SystemUserClient, { SearchSystemUserContext, SearchSystemUserItem } from "../apiClients/SystemUserClient";
import ButtonEx from "../components/ButtonEx";
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import { useAccessInfo } from "../contexts/AccessTokenProvider";
import PanelSegment from "../components/PanelSegment";
import MobileEntityCard from "../components/responsive/MobileEntityCard";
import ResponsiveCollection from "../components/responsive/ResponsiveCollection";
import { ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from "../components/responsive";
import useViewportMode from "../hooks/useViewportMode";

export interface ImpersonateUserPopupProps extends Omit<IconButtonExProps, 'onClick'> {
    onSelected?: (systemUser: SearchSystemUserItem, withAdmin: boolean) => Promise<void> | void;
    trigger?: JSX.Element;
}

const ImpersonateUserPopup = ({ onSelected = async () => { }, trigger = <ButtonEx>Impersonate</ButtonEx>, ...btnProps }: ImpersonateUserPopupProps) => {
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const theme = useTheme();
    const viewport = useViewportMode();

    const [data, setData] = useState<SearchSystemUserItem[]>([]);
    const [selectedItem, setSelectedItem] = useState<SearchSystemUserItem>(null);
    const [searchContext, setSearchContext] = useState<SearchSystemUserContext>({});
    const [withAdmin, setWithAdmin] = useState<boolean>(false);

    const { accessInfo: { currentUserId } } = useAccessInfo();
    const disableSave = selectedItem === null;

    useEffect(() => {
        const admin = localStorage.getItem('impWithAdmin')?.toLocaleLowerCase() === 'true';
        setWithAdmin(admin);
    }, [])

    const searchUsers = async () => {
        try {
            setBusy(true);
            var response = await new SystemUserClient().SearchSystemUser(searchContext);
            setData(response.filter(x => x.UserId !== currentUserId));
        } catch (error) {
            setError(error, "Failed to search for users")
        } finally {
            setBusy(false);
        }
    }
    const clearSearch = () => {
        setData([]);
        setSelectedItem(null);
        setSearchContext({});
    }

    const handleSubmit = async () => {
        try {
            setBusy(true);
            setOpen(false);
            await onSelected(selectedItem, withAdmin);
        } catch (error) {
            setError(error, "Failed to post onSelected");
        }
        finally {
            setBusy(false);
        }
    };

    const handleOpen = async () => {
        setOpen(true);
        setSearchContext({});
        setSelectedItem(null);
        setData([]);
    };

    const handleRowClicked = (params: any) => {
        setSelectedItem(params.row as SearchSystemUserItem);
    }

    const handleWithAdminChecked = () => {
        setWithAdmin(!withAdmin);
        localStorage.setItem('impWithAdmin', withAdmin ? 'false' : 'true');//it is always flipped during the event
    }

    const selectUser = (user: SearchSystemUserItem) => setSelectedItem(user);

    const renderUserCard = (user: SearchSystemUserItem) => (
        <MobileEntityCard
            ariaLabel={`Impersonation user: ${user.UserDisplayName}`}
            title={user.UserDisplayName}
            subtitle={user.Email}
            status={<Radio
                checked={selectedItem?.UserId === user.UserId && selectedItem?.OrganizationId === user.OrganizationId}
                onChange={() => selectUser(user)}
                inputProps={{ 'aria-label': `Select user: ${user.UserDisplayName} at ${user.OrganizationName}` }}
                sx={{ width: 44, height: 44 }}
            />}
            fields={[{ label: I18N.Translate('OrganizationName'), value: user.OrganizationName, fullWidth: true }]}
        />
    );

    const desktopGrid = (
        <Box height={300} border={1}>
            <DataGridPro
                density="compact"
                onRowClick={handleRowClicked}
                checkboxSelection={false}
                columns={[
                    { field: 'OrganizationName', headerName: I18N.Translate('OrganizationName'), flex: 2, editable: true },
                    { field: 'UserDisplayName', headerName: I18N.Translate('UserDisplayName'), flex: 2 },
                    { field: 'Email', headerName: I18N.Translate('Email'), flex: 2 },
                ]}
                rows={data.map(x => (
                    {
                        id: `${x.UserId}-${x.OrganizationId}`,
                        UserId: x.UserId,
                        UserDisplayName: x.UserDisplayName,
                        Email: x.Email,
                        OrganizationId: x.OrganizationId,
                        OrganizationName: x.OrganizationName
                    }
                ))}
            />
        </Box>
    );

    return <>
        <ErrorDisplay />
        <MenuItem onClick={handleOpen} dense>
            <ListItemIcon>
                <AccessibilityNewIcon />
            </ListItemIcon>
            <I18NText>Impersonate</I18NText>
        </MenuItem>
        <ResponsiveDialog
            aria-labelledby="customized-dialog-title"
            open={open}
            fullWidth
            maxWidth="md"
            tabletFullScreen="portrait"
            PaperProps={{ sx: { '& > .responsive-dialog-busy': { display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' } } }}
        >
            <BusySpinner busy={busy} className="responsive-dialog-busy">
                <ResponsiveDialogHeader id="customized-dialog-title" title={I18N.Translate("Search Users for Impersonation")} onClose={() => setOpen(false)} />
                <DialogContent dividers sx={{ p: viewport.isPhone ? 1 : theme.spacing(2), minHeight: 0, overflowY: 'auto' }}>
                    <PanelSegment titleContent={I18N.Translate("SearchUser")}>
                        <Box margin={viewport.isPhone ? 1 : 2}>
                            <Stack direction='column' spacing={2}>
                                <TextField label={I18N.Translate('FirstName')} fullWidth
                                    name="FirstName"
                                    size="small"
                                    variant="outlined"
                                    value={searchContext?.FirstName ?? ''}
                                    onChange={(e) => setSearchContext({ ...searchContext, FirstName: e.target.value })}
                                    onKeyDown={(e) => { e.stopPropagation() }} />
                                <TextField label={I18N.Translate('LastName')} fullWidth
                                    name="LastName"
                                    size="small"
                                    variant="outlined"
                                    value={searchContext?.LastName ?? ''}
                                    onChange={(e) => setSearchContext({ ...searchContext, LastName: e.target.value })}
                                    onKeyDown={(e) => { e.stopPropagation() }} />
                                <TextField label={I18N.Translate('OrganizationName')} fullWidth
                                    name="OrganizationName"
                                    size="small"
                                    variant="outlined"
                                    value={searchContext?.OrganizationName ?? ''}
                                    onChange={(e) => setSearchContext({ ...searchContext, OrganizationName: e.target.value })}
                                    onKeyDown={(e) => { e.stopPropagation() }} />
                                <TextField label={I18N.Translate('Email')} fullWidth
                                    name="Email"
                                    size="small"
                                    variant="outlined"
                                    value={searchContext?.Email ?? ''}
                                    onChange={(e) => setSearchContext({ ...searchContext, Email: e.target.value })}
                                    onKeyDown={(e) => { e.stopPropagation() }} />
                            </Stack>
                            <Box display="flex" flexWrap="wrap" gap={1} margin={2} sx={{ '& .MuiButtonBase-root': { minHeight: 44 } }}>
                                <Box flexGrow={1} />
                                <Box><ButtonEx onClick={clearSearch}><I18NText>Clear</I18NText></ButtonEx></Box>
                                <Box><ButtonEx color='secondary' onClick={searchUsers}><I18NText>Search</I18NText></ButtonEx></Box>
                            </Box>
                        </Box>
                    </PanelSegment>
                    <ResponsiveCollection
                        viewMode={viewport.isTablet && viewport.isPortrait ? 'phone' : viewport.mode}
                        items={data}
                        getKey={user => `${user.UserId}-${user.OrganizationId}`}
                        renderCard={renderUserCard}
                        desktop={desktopGrid}
                        ariaLabel="Impersonation search results"
                        emptyState={<Typography sx={{ p: 2 }} color="text.secondary">No users found</Typography>}
                    />
                </DialogContent>
                <StickyDialogActions phoneLayout="stacked" sx={{ margin: 0, padding: theme.spacing(1) }}>
                    <Box marginLeft={viewport.isPhone ? 0 : 2} flexGrow={1}><Tooltip title={I18N.Translate("tooltip_withAdmin")}><FormControlLabel control={<Checkbox checked={withAdmin} onChange={handleWithAdminChecked} sx={{ width: 44, height: 44 }} />} label={I18N.Translate("checkbox_withAdmin")} /></Tooltip></Box>
                    <Button onClick={() => setOpen(false)} variant='text' color='inherit'><I18NText>Cancel</I18NText></Button>
                    <Button disabled={disableSave} onClick={handleSubmit} variant='outlined' color='primary'><I18NText>Impersonate</I18NText> {selectedItem?.UserDisplayName}</Button>
                </StickyDialogActions>
            </BusySpinner>
        </ResponsiveDialog>
    </>;
}

export default ImpersonateUserPopup;
